import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, apiError, fmtWhen } from "@/lib/api";
import { AccessRequestPanel } from "@/components/AccessRequests";
import { ChangePassword } from "@/components/ChangePassword";
import { SuperAdmins } from "@/components/SuperAdmins";
import { useAuth } from "@/context/AuthContext";

const Field = ({ label, hint, children }) => (
  <div>
    <label className="label-caps">{label}</label>
    {children}
    {hint && <p className="mt-1 text-[10px] text-muted-foreground">{hint}</p>}
  </div>
);

export default function Profile() {
  const { user, refresh, roleLabel, superAdmin } = useAuth();
  const navigate = useNavigate();
  const [options, setOptions] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [superBusy, setSuperBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/profile/options"), api.get("/profile/me")]).then(([o, m]) => {
      setOptions(o.data);
      setForm({
        name: m.data.name || "",
        mobile: m.data.mobile || "",
        landline: m.data.landline || "",
        personnel_no: m.data.personnel_no || "",
        designation_grade: m.data.designation_grade || "",
        department: m.data.department || "",
        org_category: m.data.org_category || "",
        org_unit: m.data.org_unit || "",
        organisation: m.data.organisation || "",
        stationed_at:
          m.data.stationed_at || (o.data.stations || {})[m.data.org_unit] || "",
        profile_complete: m.data.profile_complete,
        profile_updated_at: m.data.profile_updated_at,
        super_admin_eligible: m.data.super_admin_eligible,
        super_admin_mode: m.data.super_admin_mode,
        super_admin_since: m.data.super_admin_since,
      });
    });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Picking a unit fills in where it sits, unless the person has typed a place of their own.
  const setUnit = (unit) =>
    setForm((f) => {
      const stations = options?.stations || {};
      const known = new Set(Object.values(stations));
      const mine = stations[unit];
      const replaceable = !f.stationed_at || known.has(f.stationed_at);
      return {
        ...f,
        org_unit: unit,
        stationed_at: mine && replaceable ? mine : f.stationed_at,
      };
    });

  const units =
    options?.categories?.find((c) => c.category === form?.org_category)?.units || [];
  // A mine group is worked as a set of individual mines, and those mines are its departments — so
  // somebody posted to a mine group picks their mine instead of typing a department.
  const myMines =
    (options?.sub_divisions || {})[(options?.unit_plants || {})[form?.org_unit]] || [];
  const partner = options?.is_partner;
  const first = form && !form.profile_complete;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/profile", form);
      toast.success(
        data.first_time ? "Thank you — your details are saved." : "Your details are updated.",
      );
      await refresh();
      if (data.first_time) navigate("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleSuper = async (on) => {
    setSuperBusy(true);
    try {
      await api.post("/profile/super-admin", { on });
      setForm((f) => ({ ...f, super_admin_mode: on }));
      toast[on ? "warning" : "success"](
        on
          ? "Super-Admin mode is ON — you can change anything, and nothing you do is being recorded."
          : "Super-Admin mode is off. You are back to your own mapping, and everything is recorded again.",
      );
      await refresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSuperBusy(false);
    }
  };

  if (!form || !options) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-5 sm:p-8" data-testid="profile-page">
      <div className="max-w-3xl">
        <div className="label-caps">{first ? "First sign-in" : "Your details"}</div>
        <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">
          {first ? "Please complete your joining details" : "Your profile"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {first
            ? "The portal needs these once, so that SDTD and your unit know who is working on which use case and how to reach you. Everything except your email id can be changed later from this page."
            : "Everything here can be changed except your email id, which is your identity on the portal."}
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-3 border border-border bg-card p-3">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <span className="font-data text-xs">{user?.email}</span>
          <span className="text-[11px] text-muted-foreground">
            {roleLabel}
            {user?.plant ? ` · ${user.plant}` : ""}
            {form.profile_updated_at ? ` · last updated ${fmtWhen(form.profile_updated_at)}` : ""}
          </span>
        </div>

        {form.super_admin_eligible && (
          <div
            className={`mt-4 border p-4 ${
              form.super_admin_mode
                ? "border-[hsl(var(--at-risk))] bg-[hsl(var(--at-risk)/0.07)]"
                : "border-border bg-card"
            }`}
            data-testid="super-admin-block"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-2xl">
                <div className="label-caps text-[hsl(var(--at-risk))]">
                  Super-Admin mode {form.super_admin_mode ? "· on" : "· off"}
                </div>
                <h2 className="font-display mt-1 text-base font-bold sm:text-lg">
                  Work above the rules, and leave no trail
                </h2>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                  While this is on you may edit, approve, turn down and remove anything in any unit,
                  without waiting for a SPOC — and nothing you do is written to the activity trail.
                  Switch it off and you work exactly as your own mapping allows, with everything
                  recorded as usual. The switch itself is always recorded, so the portal can say
                  when the mode was on.
                  {form.super_admin_mode && form.super_admin_since
                    ? ` On since ${fmtWhen(form.super_admin_since)}.`
                    : ""}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-data text-[11px] uppercase tracking-widest text-muted-foreground">
                  {form.super_admin_mode ? "On" : "Off"}
                </span>
                <Switch
                  checked={!!form.super_admin_mode}
                  disabled={superBusy}
                  data-testid="super-admin-toggle"
                  onCheckedChange={toggleSuper}
                />
              </div>
            </div>
          </div>
        )}

        {form.super_admin_eligible && form.super_admin_mode && <SuperAdmins />}

        <form onSubmit={save} className="mt-6 grid gap-4 sm:grid-cols-2">
          <Field label="Your name *">
            <Input
              className="mt-1 h-11 rounded-none"
              data-testid="profile-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </Field>
          <Field label="Mobile number *" hint="10 digits, the number SDTD will call you on">
            <Input
              className="font-data mt-1 h-11 rounded-none"
              data-testid="profile-mobile"
              value={form.mobile}
              onChange={(e) => set("mobile", e.target.value)}
            />
          </Field>
          {partner ? (
            <Field label="Your organisation *">
              <Input
                className="mt-1 h-11 rounded-none"
                data-testid="profile-organisation"
                value={form.organisation}
                onChange={(e) => set("organisation", e.target.value)}
              />
            </Field>
          ) : (
            <Field label="SAIL personnel number *">
              <Input
                className="font-data mt-1 h-11 rounded-none"
                data-testid="profile-personnel"
                value={form.personnel_no}
                onChange={(e) => set("personnel_no", e.target.value)}
              />
            </Field>
          )}
          <Field label={partner ? "Designation" : "Designation *"}>
            <Select
              value={form.designation_grade}
              onValueChange={(v) => set("designation_grade", v)}
            >
              <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="profile-designation">
                <SelectValue placeholder="Choose your grade" />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-popover">
                {options.designations.map((d) => (
                  <SelectItem key={d.grade} value={d.grade}>
                    {d.grade} — {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field
            label={myMines.length ? "Mine *" : "Department *"}
            hint={
              myMines.length
                ? "A mine group is worked as a set of mines — pick the one you are posted to."
                : "e.g. SMS-II, Blast Furnace, Finance, SDTD"
            }
          >
            {myMines.length ? (
              <Select value={form.department} onValueChange={(v) => set("department", v)}>
                <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="profile-department">
                  <SelectValue placeholder="Choose your mine" />
                </SelectTrigger>
                <SelectContent className="max-h-72 rounded-none bg-popover">
                  {myMines.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                className="mt-1 h-11 rounded-none"
                data-testid="profile-department"
                value={form.department}
                onChange={(e) => set("department", e.target.value)}
              />
            )}
          </Field>
          <Field label="Category *">
            <Select
              value={form.org_category}
              onValueChange={(v) => {
                set("org_category", v);
                set("org_unit", "");
              }}
            >
              <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="profile-category">
                <SelectValue placeholder="Choose" />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-popover">
                {options.categories.map((c) => (
                  <SelectItem key={c.category} value={c.category}>
                    {c.category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Plant / Unit *">
            <Select
              value={form.org_unit}
              onValueChange={setUnit}
              disabled={!form.org_category}
            >
              <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="profile-unit">
                <SelectValue placeholder={form.org_category ? "Choose" : "Choose a category first"} />
              </SelectTrigger>
              <SelectContent className="max-h-72 rounded-none bg-popover">
                {units.map((u) => (
                  <SelectItem key={u} value={u}>
                    {u}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Office telephone">
            <Input
              className="font-data mt-1 h-11 rounded-none"
              data-testid="profile-landline"
              value={form.landline}
              onChange={(e) => set("landline", e.target.value)}
            />
          </Field>
          <Field
            label="Stationed at"
            hint="Filled in from the unit you pick — change it if you sit somewhere else."
          >
            <Input
              className="mt-1 h-11 rounded-none"
              data-testid="profile-stationed"
              placeholder="e.g. Bokaro"
              value={form.stationed_at}
              onChange={(e) => set("stationed_at", e.target.value)}
            />
          </Field>
          <div className="sm:col-span-2">
            <Button
              type="submit"
              disabled={busy}
              data-testid="save-profile-button"
              className="h-12 w-full rounded-none font-display text-xs font-bold uppercase tracking-widest"
            >
              {busy ? "Saving…" : first ? "Save and enter the portal" : "Save my details"}
            </Button>
          </div>
        </form>
      </div>
      <div className="max-w-3xl">
        <ChangePassword />
        <AccessRequestPanel />
      </div>
    </div>
  );
}
